import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  Divider, 
  Chip,
  Grid,
  useTheme,
  alpha,
  LinearProgress
} from '@mui/material';
import { 
  TrendingUp, 
  CalendarToday, 
  Person, 
  Group, 
  EmojiEvents,
  CheckCircle,
  AccessTime
} from '@mui/icons-material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { motion } from 'framer-motion';

// Komponent modalu ze szczegółami celu
const GoalDetailsModal = ({ goal, userContribution, open, onClose }) => {
  const theme = useTheme();
  const progress = goal?.progress || 0;
  const isCompleted = progress >= 100;
  const isTeamGoal = goal?.type === 'team';
  
  if (!goal) return null;

  // Formatowanie daty
  const formatDate = (date) => {
    if (!date) return 'Nie określono';
    return format(new Date(date), 'd MMMM yyyy', { locale: pl });
  };

  // Dynamiczne kolory w zależności od postępu
  const getProgressColor = (progress) => {
    if (progress >= 100) return theme.palette.success.main;
    if (progress >= 75) return theme.palette.success.light;
    if (progress >= 50) return theme.palette.warning.main;
    if (progress >= 25) return theme.palette.warning.light;
    return theme.palette.error.light;
  };

  // Obliczenie pozostałego czasu
  const getRemainingTime = () => {
    if (!goal.endDate) return 'Brak terminu';
    
    const now = new Date();
    const end = new Date(goal.endDate);
    
    if (now > end) return 'Termin minął';
    
    const diffTime = Math.abs(end - now);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return `${diffDays} ${diffDays === 1 ? 'dzień' : 'dni'}`;
  };

  // Formatowanie wartości celu
  const formatValue = (value, type) => {
    if (type === 'sales_amount') {
      return new Intl.NumberFormat('pl-PL').format(value) + ' zł';
    }
    return value;
  };

  // Wyświetlanie motywacyjnego komunikatu
  const getMotivationalMessage = () => {
    if (isCompleted) return 'Gratulacje! Osiągnąłeś swój cel!';
    if (progress >= 90) return 'Jesteś już prawie na mecie! Jeszcze tylko trochę!';
    if (progress >= 75) return 'Świetny postęp! Kontynuuj dobrą passę!';
    if (progress >= 50) return 'Jesteś w połowie drogi! Nie poddawaj się!';
    if (progress >= 25) return 'Dobry start! Trzymaj tempo!';
    return 'Każdy krok przybliża Cię do celu!';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      {/* Nagłówek z typem celu */}
      <DialogTitle 
        sx={{ 
          bgcolor: isTeamGoal ? theme.palette.primary.main : getProgressColor(progress),
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isTeamGoal ? <EmojiEvents sx={{ mr: 1 }} /> : <TrendingUp sx={{ mr: 1 }} />}
          <Typography variant="h6">
            {isTeamGoal ? 'Cel zespołowy' : 'Cel indywidualny'}: {goal.name}
          </Typography>
        </Box>
        
        {isCompleted && (
          <Chip 
            icon={<CheckCircle />} 
            label="Osiągnięto!" 
            color="success" 
            size="small"
            sx={{ bgcolor: 'white', color: theme.palette.success.dark }}
          />
        )}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ py: 2 }}>
          {/* Opis celu */}
          <Typography variant="body1" paragraph>
            {goal.description}
          </Typography>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Informacje o celu */}
          <Grid container spacing={3}>
            {/* Lewa kolumna - podstawowe informacje */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Informacje o celu
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarToday fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                <Typography variant="body2">
                  <strong>Okres:</strong> {formatDate(goal.startDate)} - {formatDate(goal.endDate)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccessTime fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                <Typography variant="body2">
                  <strong>Pozostało:</strong> {getRemainingTime()}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {isTeamGoal ? (
                  <Group fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                ) : (
                  <Person fontSize="small" sx={{ mr: 1, color: theme.palette.text.secondary }} />
                )}
                <Typography variant="body2">
                  <strong>Typ:</strong> {isTeamGoal ? 'Zespołowy' : 'Indywidualny'}
                </Typography>
              </Box>
              
              {goal.reward && (
                <Box sx={{ mt: 2 }}>
                  <Chip 
                    icon={<EmojiEvents />} 
                    label={`Nagroda: ${goal.reward}`}
                    color="secondary"
                    variant="outlined"
                  />
                </Box>
              )}
            </Grid>
            
            {/* Prawa kolumna - postęp */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Postęp
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">
                    {goal.targetTypeLabel || 'Postęp'}:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color={getProgressColor(progress)}>
                    {progress.toFixed(1)}%
                  </Typography>
                </Box>
                
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(progress, 100)} 
                  sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    bgcolor: alpha(theme.palette.grey[300], 0.5),
                    '& .MuiLinearProgress-bar': {
                      bgcolor: getProgressColor(progress),
                      borderRadius: 5
                    }
                  }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {formatValue(goal.currentValue || 0, goal.targetType)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    z {formatValue(goal.targetValue, goal.targetType)}
                  </Typography>
                </Box>
              </Box>
              
              {/* Twój wkład w cel zespołowy */}
              {isTeamGoal && userContribution && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Twój wkład:
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      Procent realizacji:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color={theme.palette.secondary.main}>
                      {userContribution.progress?.toFixed(1)}%
                    </Typography>
                  </Box>
                  
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(userContribution.progress || 0, 100)} 
                    sx={{ 
                      height: 10, 
                      borderRadius: 5,
                      bgcolor: alpha(theme.palette.grey[300], 0.5),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: theme.palette.secondary.main,
                        borderRadius: 5
                      }
                    }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {formatValue(userContribution.currentValue || 0, goal.targetType)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Sekcja motywacyjna */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Box 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: alpha(getProgressColor(progress), 0.1),
                border: `1px solid ${alpha(getProgressColor(progress), 0.3)}`,
                textAlign: 'center'
              }}
            >
              <Typography variant="h6" color={getProgressColor(progress)}>
                {getMotivationalMessage()}
              </Typography>
              
              {goal.motivationalTip && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {goal.motivationalTip}
                </Typography>
              )}
            </Box>
          </motion.div>
          
          {/* Historia postępu */}
          {goal.history && goal.history.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Historia postępu
              </Typography>
              
              {goal.history.map((entry, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ minWidth: 100 }}>
                    {formatDate(entry.date)}
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    {entry.description || `Postęp: ${formatValue(entry.value, goal.targetType)}`}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
        <Button onClick={onClose} color="primary">
          Zamknij
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoalDetailsModal;
